/**
 * viewer-origin — which HOST serves `/v/<id>`.
 *
 * User-uploaded and agent-written HTML is active content. Served from the app
 * origin it would share cookies, storage and same-origin `/api/*` with the
 * signed-in app. Two layers keep it apart, both always on:
 *
 *  1. The route itself sends `Content-Security-Policy: sandbox …` without
 *     `allow-same-origin`, so the document gets a unique opaque origin
 *     wherever it is served (see `app/v/[fileId]/route.ts`).
 *  2. When `NEXT_PUBLIC_VIEWER_ORIGIN` names a cookieless host (a second
 *     domain on the same deployment, e.g. `https://view.vestra.ai`), every
 *     viewer link — share links, the preview iframe, open-in-new-tab — is
 *     minted on it, so the HTML is cross-site as well as opaque.
 *
 * Unset → relative `/v/<id>`, exactly what shipped before. The env is read at
 * call time (a module-scope read is captured once and cannot be stubbed by a
 * test) and via the literal `process.env.NEXT_PUBLIC_VIEWER_ORIGIN`, which is
 * the only form Next inlines into the browser bundle.
 */

/** The viewer host without a trailing slash, or '' when the env is unset. */
export function viewerOrigin(): string {
  const raw = process.env.NEXT_PUBLIC_VIEWER_ORIGIN ?? '';
  return raw.trim().replace(/\/+$/, '');
}

/** The canonical viewer URL for a file id — absolute on the viewer host when
 *  one is configured, otherwise the relative same-origin proxy path. */
export function viewerUrl(publicId: string): string {
  return `${viewerOrigin()}/v/${publicId}`;
}

/** Rewrite an already-minted relative `/v/…` path (with any query) onto the
 *  viewer host. Anything else (signed CDN URLs, blob URLs) passes through. */
export function viewerHref(pathOrUrl: string): string {
  const origin = viewerOrigin();
  if (!origin || !pathOrUrl.startsWith('/v/')) return pathOrUrl;
  return `${origin}${pathOrUrl}`;
}

/** An absolute, copy-to-clipboard share link: the viewer host when set, else
 *  the canonical site URL, else the page's own origin. */
export function shareUrl(publicId: string): string {
  const origin = viewerOrigin()
    || process.env.NEXT_PUBLIC_SITE_URL
    || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/v/${publicId}`;
}
